import React, { useState } from 'react';
import { CleverAuthType, CleverService } from '../../services/clever.service';
import { Toast } from './Toast';
import Loader from './Loader';

export enum CleverLoginButtonType {
  DEFAULT_SIGN_IN = 'default_sign_in',
  BORDER_SIGN_IN = 'border_sign_in',
}

interface CleverLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
  children?: React.ReactNode;
  redirectUrl?: string;
  type?: CleverLoginButtonType;
  authType: CleverAuthType;
  title?: string;
}

const CleverLoginButton: React.FC<CleverLoginButtonProps> = ({
  onSuccess,
  onError,
  className = 'text-sm font-medium font-manrope rounded-lg px-4 py-1.5',
  children,
  redirectUrl = '/',
  type = CleverLoginButtonType.DEFAULT_SIGN_IN,
  authType,
  title = '',
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleCleverLogin = async () => {
    try {
      setIsLoading(true);

      // Store redirect URL for after authentication
      localStorage.setItem('clever_redirect_url', redirectUrl);

      // Initiate Clever OAuth flow
      // await CleverService.initiateAuth(authType);

      const URL = `${process.env.REACT_APP_API_URL}/api/auth/clever/session`;

      // Open new tab for Clever authentication
      const newTab = window.open(URL, '_blank');
      
      // Check if tab was blocked
      if (!newTab || newTab.closed || typeof newTab.closed == 'undefined') {
        Toast('Please allow popups for this site or manually navigate to the authentication page', true);
        onError?.('Tab was blocked by browser. Please allow popups and try again.');
      }
      
      setIsLoading(false);



      // Note: The page will redirect to Clever, so onSuccess won't be called here
      // It will be called in the callback component after successful authentication
    } catch (error: any) {
      console.error('Error initiating Clever login:', error);
      const errorMessage = error.message || 'Failed to start Clever login';
      Toast(errorMessage, true);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleCleverLogin}
      disabled={isLoading}
      className={`
        w-full flex items-center justify-center border-2 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors
        ${
          type === CleverLoginButtonType.DEFAULT_SIGN_IN
            ? 'text-white bg-secondaryStoryAdditionalLightBlueText focus:ring-secondaryStoryAdditionalLightBlueText border-secondaryStoryAdditionalLightBlueText'
            : 'text-secondaryStoryAdditionalLightBlueText bg-transparent border-1 border-secondaryStoryAdditionalLightBlueText focus:ring-secondaryStoryAdditionalLightBlueText'
        }
        ${className}
      `}
      title={title}
    >
      <div className="flex items-center font-bold">
        <div className="pr-2 pb-0.5">
          {type === CleverLoginButtonType.DEFAULT_SIGN_IN ? (
            <svg width="24" height="24" viewBox="5 0 75 75" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M70 20C65 15 58 10 50 10C33.43 10 20 23.43 20 40C20 56.57 33.43 70 50 70C58 70 65 65 70 60L60 50C56 54 52 55 50 55C41.72 55 35 48.28 35 40C35 31.72 41.72 25 50 25C52 25 56 26 60 30L70 20Z"
                fill="white"
              />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="5 0 75 75" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M70 20C65 15 58 10 50 10C33.43 10 20 23.43 20 40C20 56.57 33.43 70 50 70C58 70 65 65 70 60L60 50C56 54 52 55 50 55C41.72 55 35 48.28 35 40C35 31.72 41.72 25 50 25C52 25 56 26 60 30L70 20Z"
                fill="#166dff"
              />
            </svg>
          )}
        </div>
        <div className="pt-0.5 flex">
          {isLoading ? (
            <>
              <Loader loader={isLoading} />
              Connecting to Clever...
            </>
          ) : (
            <span>{children || 'Sign in with Clever'}</span>
          )}
        </div>
      </div>
    </button>
  );
};

export default CleverLoginButton;
